import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams, useOutletContext } from "react-router-dom";

import "../../App.css";
import MasterCardImage from "../../assets/images/masterCards.png";
import VisaImage from "../../assets/images/visa.png";
import VerveImage from "../../assets/images/verve.png";
import AfrigoImage from "../../assets/images/afrigo-logo.png";
import useHttp from "../../hooks/use-http";
import CardContext from "../../store/card-context";
import { useTitle } from "../../hooks/useTitle";
import toast from "react-hot-toast";
import Spinner from "../Spinner";
import { usePaymentSession } from "./usePaymentSession";
import PaymentContext from "../../store/PaymentContext";

const CardPayment = () => {
  const [cardNumber, setCardNumber] = useState("");
  const [expiryDate, setExpiryDate] = useState("");
  const [cvv, setCvv] = useState("");
  const [cardType, setCardType] = useState("");
  const [saveCard, setSaveCard] = useState(false);
  const [paymentSource, setPaymentSource] = useState("new");
  const [savedCards, setSavedCards] = useState([]);
  const [selectedSavedPaymentId, setSelectedSavedPaymentId] = useState(null);
  const [transactionInfo, setTransactionInfo] = useState([]);
  const { sendRequest: fetchTransactionDetails } = useHttp();
  const {
    isLoading: transactionLoading,
    sendRequest: CardPayment,
  } = useHttp();
  const { isLoading: savedCardsLoading, sendRequest: fetchSavedCards } =
    useHttp();
  const { sendRequest: MerchantInfo } = useHttp();
  const { isLoading: schemeloading, sendRequest: PaymentScheme } = useHttp();
  const navigate = useNavigate();
  const { setCardDetails, setToken } = useContext(CardContext);
  const [merchantId, setMerchantId] = useState();
  const [schemes, setSchemes] = useState([]);
  const [loading, setLoading] = useState(false);
  const { channel } = useContext(PaymentContext);

  const { id } = useParams();
  useTitle("Card");

  const cardNumberRef = useRef(null);
  const expiryDateRef = useRef(null);
  const cvvRef = useRef(null);

  // Use our custom hooks
  const { markPaymentSubmitted } = usePaymentSession(id);

  const getMerchantInfo = useCallback(
    (data) => {
      setMerchantId(data?.data?.id);
    },
    [setMerchantId]
  );

  const getPaymentScheme = useCallback(
    (data) => {
      setSchemes(data?.data);
    },
    [setSchemes]
  );

  const fetchMerchantInfo = useCallback(async () => {
    MerchantInfo(
      {
        url: `${process.env.REACT_APP_BASE_URL}/api/transaction/merchant/${id}`,
        headers: {
          "Content-Type": "application/json",
        },
      },
      getMerchantInfo
    );
  }, [MerchantInfo, getMerchantInfo, id]);

  const fetchPaymentScheme = useCallback(async () => {
    PaymentScheme(
      {
        url: `${process.env.REACT_APP_BASE_URL}/api/payment-scheme/merchant?merchantId=${merchantId}`,
        headers: {
          "Content-Type": "application/json",
        },
      },
      getPaymentScheme
    );
  }, [PaymentScheme, getPaymentScheme, merchantId]);
  useEffect(() => {
    const fetchData = async () => {
      await fetchMerchantInfo();
    };

    fetchData();
  }, [fetchMerchantInfo]);

  useEffect(() => {
    if (!merchantId) return;
    const fetchScheme = async () => {
      await fetchPaymentScheme();
    };

    fetchScheme();
  }, [fetchPaymentScheme, merchantId]);

  const getTransactionInfo = (data) => {
    setTransactionInfo(data);
  };
  const getSavedCards = useCallback(
    (data) => {
      setSavedCards(Array.isArray(data?.cards) ? data.cards : []);
    },
    [setSavedCards],
  );
  const fetchTransactionInfo = useCallback(async () => {
    fetchTransactionDetails(
      {
        url: `${process.env.REACT_APP_BASE_URL}/api/transaction/${id}?paymentMethod=${channel}`,
        headers: {
          "Content-Type": "application/json",
        },
      },
      getTransactionInfo
    );
  }, [fetchTransactionDetails, id]);

  useEffect(() => {
    if (!channel || !merchantId) return;
    const fetchData = async () => {
      await fetchTransactionInfo();
    };

    fetchData();
  }, [fetchTransactionInfo, channel, merchantId]);

  useEffect(() => {
    const email = transactionInfo?.data?.email;
    if (!merchantId || !email) return;

    fetchSavedCards(
      {
        url: `${process.env.REACT_APP_BASE_URL}/api/merchants/${merchantId}/cards/by-email?email=${encodeURIComponent(
          email,
        )}`,
        headers: {
          "Content-Type": "application/json",
        },
      },
      getSavedCards,
    );
  }, [
    fetchSavedCards,
    getSavedCards,
    merchantId,
    transactionInfo?.data?.email,
  ]);

  const types = {
    visa: /^4/,
    verve: /(^506|^507|^650)[0-9]{0,16}/,
    mastercard: /^5[1-5]/,
    //    'discover': /(6011|65|64[4-9]|622)[0-9]/,
    amex: /^3[47]/,
    unionpay: /^62/,
    afrigo: /^5640/,
    payattitudecard: /^9800/,
  };
  console.log(cardType, 'cardType')
  const handleCardNumberChange = (event) => {
    let inputValue = event.target.value.replace(/\D/g, ""); // Remove non-numeric characters

    let detectedCardType = "";
    for (const [type, pattern] of Object.entries(types)) {
      if (pattern.test(inputValue)) {
        detectedCardType = type;
        break;
      }
    }

    setCardType(detectedCardType);

    inputValue = inputValue
      .replace(/\s/g, "")
      .replace(/(\d{4})/g, "$1 ")
      .trim();

    // Determine maximum length based on card type
    let maxLength;
    switch (detectedCardType) {
      case "visa":
      case "mastercard":
      case "unionpay":
      case "payattitudecard":
        maxLength = 19;
        break;
      case "verve":
        maxLength = 23;
        break;
      case "afrigo":
        maxLength = 22;
        break;
      case "amex":
        maxLength = 17;

        break;
      default:
        maxLength = 23; // Default to 23 characters
        break;
    }

    // Truncate the input if it exceeds the maximum length
    if (inputValue.length > maxLength) {
      inputValue = inputValue.slice(0, maxLength);
    }

    setCardNumber(inputValue);
    if (inputValue.length === maxLength) {
      expiryDateRef.current.focus();
    }
  };

  const handleExpiryDateChange = (event) => {
    let inputValue = event.target.value.replace(/\D/g, ""); // Remove non-numeric characters
    let formattedValue = inputValue;

    if (inputValue.length > 2) {
      formattedValue =
        inputValue.substring(0, 2) + "/" + inputValue.substring(2, 4);
    }

    if (formattedValue.length <= 5) {
      setExpiryDate(formattedValue);
    }

    if (formattedValue.length === 5) {
      cvvRef.current.focus();
    }
  };

  const handleCvvChange = (event) => {
    const inputValue = event.target.value;

    const maxCvvLength = cardType === "amex" ? 4 : 3;

    // Use regex to ensure it's a number and limit to 3 digits
    const sanitizedValue = inputValue
      .replace(/\D/g, "")
      .substring(0, maxCvvLength);

    setCvv(sanitizedValue);
  };

  const processCard = (data) => {
    if (data) {
      markPaymentSubmitted();
      if (
        cardType === "mastercard" ||
        cardType === "visa" ||
        cardType === "unionpay" ||
        cardType === "amex"
      ) {
        window.location.replace(data?.data?.returnUrl);
      } else {
        window.location.replace(data?.data?.returnUrl);
      }
    }
  };

  const processPayattitude = (data) => {
    if (data) {
      setToken(data?.data?.token);
      navigate(`/payattitude/otp/${id}`);
    }
  };

  const payTransaction = (event) => {
    event.preventDefault();
    setLoading(true);

    let transaction;

    if (paymentSource === "saved") {
      if (!selectedSavedPaymentId) {
        toast.error("Please select a saved card");
        setLoading(false);
        return;
      }

      transaction = {
        id,
        paymentId: selectedSavedPaymentId,
        scheme: cardType,
      };
    } else {
      if (!cardType) {
        toast.error("Invalid or unsupported card type");
        setLoading(false);
        return;
      }

      // Check if expiryDate is not empty and doesn't match MM/YY format
      if (!expiryDate || !/^\d{2}\/\d{2}$/.test(expiryDate)) {
        toast.error("Invalid expiry date (MM/YY format required)");
        setLoading(false);
        return;
      }

      // Check if cvv is empty or less than required length based on cardType
      const requiredCvvLength = cardType === "amex" ? 4 : 3;
      if (!cvv || cvv.length < requiredCvvLength) {
        toast.error(
          `CVV must be ${requiredCvvLength} digits for ${cardType.toUpperCase()}`
        );
        setLoading(false);
        return;
      }

      // Check if cardNumber, expiryDate, and cvv are not empty
      if (!cardNumber || !expiryDate || !cvv) {
        toast.error("Please fill in all required fields");
        setLoading(false);
        return;
      }

      transaction = {
        id,
        scheme: cardType,
        cardNumber: cardNumber.replace(/\s/g, ""),
        expiryDate,
        cvv,
        saveCard,
        encryptedData: "",
      };
    }

    setCardDetails(transaction);

    if (cardType === "payattitudecard") {
      CardPayment(
        {
          url: `${process.env.REACT_APP_BASE_URL}/api/transaction/postCardDetail/${id}`,
          method: "POST",
          body: transaction,
          headers: { "Content-Type": "application/json" },
        },
        processPayattitude
      );
      return;
    }

    if (cardType === "verve" || cardType === "afrigo") {
      setLoading(false);
      navigate(`/payment/card-transaction/${id}`);
      return;
    }

    CardPayment(
      {
        url: `${process.env.REACT_APP_BASE_URL}/api/transaction/postCardDetail/${id}`,
        method: "POST",
        body: transaction,
        headers: { "Content-Type": "application/json" },
      },
      processCard
    );
  };


  const currentCurrencySymbol = transactionInfo?.data?.currency === "NGN" ? "₦" : (transactionInfo?.data?.currency || "NGN");
  const totalAmountValue = (transactionInfo?.data?.amount || 0) + (transactionInfo?.data?.fee || 0);

  return (
    <div className="w-full max-w-[560px] mx-auto py-2">
      <h2 className="text-xl font-bold text-center text-slate-800 mb-6">
        Enter your card details to pay
      </h2>

      <form onSubmit={payTransaction} className="space-y-5">
        {/* Toggle between saved card and new card details */}
        {savedCards.length > 0 && (
          <div
            onClick={() => {
              if (paymentSource === "saved") {
                setPaymentSource("new");
                setSelectedSavedPaymentId(null);
              } else {
                setPaymentSource("saved");
                setSaveCard(false);
              }
            }}
            className="flex items-center gap-3 cursor-pointer py-3 border-b border-slate-100 mb-5 select-none"
          >
            <div className="relative flex items-center justify-center flex-shrink-0">
              <input
                type="checkbox"
                id="use_saved_card"
                checked={paymentSource === "saved"}
                onChange={() => {}}
                className="sr-only"
              />
              <div
                className={`w-[18px] h-[18px] rounded border flex items-center justify-center transition-colors ${
                  paymentSource === "saved"
                    ? "bg-[#00aeef] border-[#00aeef]"
                    : "border-slate-300 bg-white"
                }`}
              >
                {paymentSource === "saved" && (
                  <svg className="w-3.5 h-3.5 text-white pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3.5">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            </div>
            <span className="text-sm font-bold text-slate-700">
              Use saved Card
            </span>
          </div>
        )}

        {/* Saved Cards Selection List */}
        {paymentSource === "saved" && (
          <div className="bg-[#f0f9ff]/50 p-4 rounded-xl border border-[#d0ecfa] overflow-y-auto max-h-[200px] space-y-2 mb-5">
            {savedCardsLoading ? (
              <p className="text-xs text-slate-500">Loading saved cards...</p>
            ) : savedCards.length === 0 ? (
              <p className="text-xs text-slate-500">No saved cards found for this email.</p>
            ) : (
              savedCards.map((card) => (
                <label
                  key={card?.paymentId ?? card?.id}
                  className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer active:scale-[0.99] transition-all duration-200 ${
                    selectedSavedPaymentId === card?.paymentId
                      ? "border-[#00aeef] bg-white shadow-sm"
                      : "border-slate-200 bg-white/70 hover:bg-white"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="radio"
                      name="saved_card_choice"
                      checked={selectedSavedPaymentId === card?.paymentId}
                      onChange={() => {
                        setSelectedSavedPaymentId(card?.paymentId);
                        setCardType(card?.scheme?.toLowerCase() || "");
                      }}
                      className="w-4 h-4 text-[#00aeef] cursor-pointer"
                    />
                    <div>
                      <p className="text-xs font-bold text-slate-800">
                        {card?.scheme?.toUpperCase() || "CARD"} •••• {card?.maskedPAN || card?.pan || ""}
                      </p>
                      <p className="text-[10px] text-slate-400 mt-0.5">
                        Expires {card?.expirationDate || card?.expiry || "--"}
                      </p>
                    </div>
                  </div>
                </label>
              ))
            )}
          </div>
        )}

        {/* Input Details */}
        {paymentSource === "new" && (
          <div className="space-y-4">
            {/* Card Number Input */}
            <div className="relative flex items-end border border-slate-200 rounded-lg px-4 py-3 bg-white focus-within:border-[#00aeef] transition-colors">
              <div className="flex-1 flex flex-col justify-center">
                <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wide leading-none">
                  Card Number
                </span>
                <input
                  type="text"
                  id="card-number"
                  placeholder="0000 0000 0000 0000"
                  className="w-full focus:outline-none text-sm font-semibold text-slate-700 bg-transparent placeholder:font-normal placeholder:text-slate-300 p-0 mt-1 tracking-widest placeholder:tracking-normal"
                  value={cardNumber}
                  onChange={handleCardNumberChange}
                  ref={cardNumberRef}
                  required
                />
              </div>
              <div className="flex gap-1.5 items-center ml-2 opacity-80">
                <img src={MasterCardImage} alt="Mastercard" className="h-[14px] w-auto object-contain" />
                <img src={VisaImage} alt="Visa" className="h-[14px] w-auto object-contain" />
                <img src={VerveImage} alt="Verve" className="h-[14px] w-auto object-contain" />
                <img src={AfrigoImage} alt="Afrigo" className="h-[14px] w-auto object-contain" />
              </div>
            </div>

            {/* Expiry & CVV Row */}
            <div className="flex gap-4">
              <div className="flex-1 relative flex items-center border border-slate-200 rounded-lg px-4 py-3 bg-white focus-within:border-[#00aeef] transition-colors">
                <div className="flex-1 flex flex-col justify-center">
                  <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wide leading-none">
                    Card Expiry
                  </span>
                  <input
                    type="text"
                    id="expiry-date"
                    placeholder="MM / YY"
                    className="w-full focus:outline-none text-sm font-semibold text-slate-700 bg-transparent placeholder:font-normal placeholder:text-slate-300 p-0 mt-1"
                    value={expiryDate}
                    onChange={handleExpiryDateChange}
                    ref={expiryDateRef}
                    maxLength="5"
                    required
                  />
                </div>
              </div>

              <div className="flex-1 relative flex items-center border border-slate-200 rounded-lg px-4 py-3 bg-white focus-within:border-[#00aeef] transition-colors">
                <div className="flex-1 flex flex-col justify-center">
                  <div className="flex justify-between items-center w-full">
                    <span className="block text-[10px] font-bold text-slate-400 uppercase tracking-wide leading-none">
                      CVV
                    </span>
                    <span
                      onClick={() => toast("Your 3-digit CVV number is located on the back of your card.")}
                      className="text-[10px] text-[#00aeef] font-medium cursor-pointer hover:underline uppercase leading-none"
                    >
                      HELP?
                    </span>
                  </div>
                  <input
                    type="text"
                    id="cvv"
                    placeholder={cardType === "amex" ? "1234" : "123"}
                    className="w-full focus:outline-none text-sm font-semibold text-slate-700 bg-transparent placeholder:font-normal placeholder:text-slate-300 p-0 mt-1"
                    value={cvv}
                    onChange={handleCvvChange}
                    ref={cvvRef}
                    maxLength={cardType === "amex" ? 4 : 3}
                    required
                  />
                </div>
              </div>
            </div>

            <div
              onClick={() => setSaveCard((prev) => !prev)}
              className="flex items-start gap-3 pt-1 cursor-pointer select-none"
            >
              <div className="relative flex items-center justify-center mt-0.5 flex-shrink-0">
                <input
                  type="checkbox"
                  id="saveConsent"
                  checked={saveCard}
                  onChange={() => {}}
                  className="sr-only"
                />
                <div
                  className={`w-[18px] h-[18px] rounded border flex items-center justify-center transition-colors ${
                    saveCard
                      ? "bg-[#00aeef] border-[#00aeef]"
                      : "border-slate-300 bg-white"
                  }`}
                >
                  {saveCard && (
                    <svg className="w-3.5 h-3.5 text-white pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="3.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </div>
              <span className="text-xs text-slate-600 leading-relaxed">
                Securely save my card details for a faster checkout experience.
              </span>
            </div>
          </div>
        )}

        {loading && (
          <p className="text-center text-xs text-[#00aeef] animate-pulse">
            Processing payment, please do not refresh...
          </p>
        )}

        <button
          type="submit"
          className="w-full h-[54px] bg-[#00aeef] hover:bg-[#0096ce] active:scale-[0.99] text-white rounded-lg font-bold text-base cursor-pointer transition-all disabled:opacity-75 disabled:cursor-not-allowed flex items-center justify-center"
          disabled={loading || transactionLoading}
        >
          {loading || transactionLoading ? (
            <Spinner />
          ) : (
            `Pay ${currentCurrencySymbol}${totalAmountValue.toLocaleString()}`
          )}
        </button>

        <p className="text-center text-[11px] text-slate-400 font-medium flex items-center justify-center gap-1 mt-2">
          <svg width="10" height="10" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect y="5" width="14" height="11" rx="2" fill="#00aeef" />
            <path d="M3 5V4.5C3 2.567 4.567 1 6.5 1V1C8.433 1 10 2.567 10 4.5V5" stroke="#00aeef" strokeWidth="2" strokeLinecap="round" />
            <circle cx="7" cy="10.5" r="1.5" fill="white" />
          </svg>  Your connection is encrypted and secure
        </p>

        <p className="text-center text-[12px] text-black leading-relaxed font-normal max-w-[460px] mx-auto">
          You'll be redirected to your bank's secure page to enter a One-Time Password (OTP). Check your SMS or email for the code and enter it to complete the transaction.
        </p>
      </form>
    </div>
  );
};

export default CardPayment;
